import { Calculator, ClipboardList, Globe } from "lucide-react";
import { Link } from "react-router-dom";
import ServicePageLayout from "../components/ServicePageLayout";

const prestations = [
    {
        icon: Calculator,
        title: "Décomptes TVA",
        desc: "Établissement de vos décomptes trimestriels ou semestriels selon la méthode effective ou la méthode forfaitaire.",
    },
    {
        icon: ClipboardList,
        title: "Immatriculation TVA",
        desc: "Accompagnement pour l'immatriculation auprès de l'AFC, en Suisse ou depuis l'étranger.",
    },
    {
        icon: Globe,
        title: "Représentation fiscale TVA",
        desc: "Fiduciaire Bleich agit comme représentant fiscal TVA pour les entreprises étrangères assujetties en Suisse.",
    },
];

const etapes = [
    {
        num: "01",
        title: "Analyse de votre activité",
        desc: "Détermination de votre régime TVA.",
    },
    {
        num: "02",
        title: "Immatriculation et mise en place",
        desc: "Immatriculation si nécessaire et mise en place du suivi.",
    },
    {
        num: "03",
        title: "Décomptes aux échéances",
        desc: "Établissement et dépôt des décomptes aux échéances, en cohérence avec votre comptabilité.",
    },
];

const pourquoiCols = [
    {
        title: "Rigueur et conformité",
        desc: "Chaque décompte est établi avec soin pour éviter les erreurs et les risques de redressement lors d'un contrôle de l'AFC.",
    },
    {
        title: "Entreprises étrangères",
        desc: "Fiduciaire Bleich accompagne régulièrement des sociétés étrangères qui ont besoin d'un représentant fiscal TVA en Suisse.",
    },
    {
        title: "Intégration comptable",
        desc: (
            <>
                Les décomptes TVA sont établis en cohérence directe avec votre{" "}
                <Link
                    to="/comptabilite"
                    data-testid="link-comptabilite"
                    className="text-[#1C9CAC] hover:underline"
                >
                    comptabilité
                </Link>
                , pour une vision financière sans écart.
            </>
        ),
    },
];

const faqs = [
    {
        q: "À partir de quel chiffre d'affaires faut-il s'assujettir à la TVA en Suisse ?",
        a: "L'assujettissement à la TVA devient en principe obligatoire dès CHF 100'000 de chiffre d'affaires annuel. Fiduciaire Bleich vérifie votre situation et vous accompagne pour l'immatriculation le moment venu.",
    },
    {
        q: "Quelle différence entre la méthode effective et la méthode forfaitaire ?",
        a: "La méthode effective consiste à déclarer la TVA encaissée et à déduire la TVA payée sur les achats. La méthode forfaitaire applique un taux simplifié, ce qui réduit le travail administratif. Fiduciaire Bleich vous conseille la méthode la plus avantageuse.",
    },
    {
        q: "Une entreprise étrangère doit-elle s'immatriculer à la TVA en Suisse ?",
        a: "Selon son activité en Suisse, une entreprise étrangère peut être assujettie et doit alors désigner un représentant fiscal en Suisse. Fiduciaire Bleich assure ce rôle de représentant fiscal TVA.",
    },
    {
        q: "À quelle fréquence faut-il établir les décomptes TVA ?",
        a: "La fréquence dépend de la méthode choisie, généralement trimestrielle pour la méthode effective et semestrielle pour la méthode forfaitaire. Fiduciaire Bleich gère ces échéances pour vous.",
    },
];

const serviceJsonLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: "TVA à Genève",
    serviceType: "Décomptes TVA, immatriculation et représentation fiscale TVA",
    provider: {
        "@type": "AccountingService",
        name: "Fiduciaire Bleich Sàrl",
        url: "https://fiduciaire-bleich.ch",
    },
    areaServed: { "@type": "AdministrativeArea", name: "Canton de Genève" },
    url: "https://fiduciaire-bleich.ch/tva",
};

const breadcrumbJsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
        { "@type": "ListItem", position: 1, name: "Accueil", item: "https://fiduciaire-bleich.ch/" },
        { "@type": "ListItem", position: 2, name: "Nos services", item: "https://fiduciaire-bleich.ch/" },
        { "@type": "ListItem", position: 3, name: "TVA", item: "https://fiduciaire-bleich.ch/tva" },
    ],
};

const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
};

const Tva = () => {
    return (
        <ServicePageLayout
            testIdPrefix="tva"
            seo={{
                title: "TVA à Genève, décomptes et conseil | Fiduciaire Bleich",
                description:
                    "Décomptes TVA, immatriculation, conseil et représentation fiscale TVA pour entreprises suisses et étrangères à Genève. Fiduciaire Bleich depuis 2004.",
                canonical: "https://fiduciaire-bleich.ch/tva",
            }}
            jsonLd={{ service: serviceJsonLd, breadcrumb: breadcrumbJsonLd, faq: faqJsonLd }}
            breadcrumb="TVA"
            hero={{
                eyebrow: "Nos services",
                h1Prefix: "TVA à",
                h1Italic: "Genève.",
                subtitle: "Décomptes rigoureux et conseil TVA pour votre entreprise.",
                intro:
                    "La TVA est une obligation administrative exigeante, source d'erreurs fréquentes et de risques de redressement. Fiduciaire Bleich gère vos décomptes TVA avec précision et vous conseille sur les aspects les plus complexes, y compris pour les entreprises étrangères actives en Suisse.",
            }}
            prestations={prestations}
            prestationsHeading={
                <>
                    Ce que <span className="italic">nous faisons.</span>
                </>
            }
            etapes={etapes}
            processHeading={
                <>
                    Une gestion TVA <span className="italic">maîtrisée.</span>
                </>
            }
            pourquoiHeading={
                <>
                    La TVA sans
                    <br />
                    <span className="italic">mauvaises surprises.</span>
                </>
            }
            pourquoiCols={pourquoiCols}
            faqHeading={
                <>
                    Questions fréquentes
                    <br />
                    sur la <span className="italic">TVA.</span>
                </>
            }
            faqs={faqs}
            cta={{
                heading: (
                    <>
                        Parlons de votre <span className="italic">TVA.</span>
                    </>
                ),
                text: "Un premier entretien gratuit et confidentiel permet de clarifier vos obligations TVA et la manière dont Fiduciaire Bleich peut les prendre en charge. Sans engagement.",
            }}
        />
    );
};

export default Tva;
